import React, { ReactNode, Fragment, useState } from 'react'
import { css } from '@emotion/css'
import { Tag } from '../../../legacy-design-system/tags'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { theme as deprecatedTheme } from '../../../styled'
import { WithTooltip } from '../../../design-system'
import { DrilldownAttackEvent } from './drilldown-content-view'
import { TitleString } from './title-string'
import { EventMetadata } from './metadata-list'

const smallScreenMediaQuery = '@media (max-width: 992px)'

export function DrilldownHeader({
  showWhiteBackgroundColor,
  onRemoveTag,
  attackEvent: {
    type,
    attackers,
    sourceIP,
    sourceName,
    displayName,
    result,
    received,
    application,
    url,
    server,
    httpRequest,
    tags = []
  },
  renderActions
}: {
  readonly showWhiteBackgroundColor: boolean
  readonly onRemoveTag: (tag: string) => void
  readonly organizationId: string
  readonly attackEvent: DrilldownAttackEvent
  readonly renderActions: ReactNode
}) {
  const [isTitleTruncated, setIsTitleTruncated] = useState(false)
  return (
    <div
      className={css`
        display: flex;
        flex-direction: column;
        padding-bottom: 20px;
        background-color: ${!!showWhiteBackgroundColor
          ? theme.color.white
          : 'initial'};
      `}
    >
      <div
        className={css`
          display: flex;
        `}
      >
        <div
          className={css`
            flex: 1;
            display: inline-flex;
            align-items: center;
            min-width: 0;
            & > * {
              margin-bottom: 10px;
            }
          `}
        >
          <WithTooltip
            disabled={!isTitleTruncated}
            renderTooltipContent={() => (
              <TitleString
                type={type}
                sourceIP={sourceIP}
                sourceName={sourceName}
                displayName={displayName}
                copyButton
              />
            )}
            children={({ ref: tooltipRef, onMouseEnter, onMouseLeave }) => (
              <div
                ref={(_ref) => {
                  if (typeof tooltipRef === 'function') {
                    tooltipRef(_ref)
                  }
                  if (_ref) {
                    const is = _ref.offsetWidth < _ref.scrollWidth
                    if (isTitleTruncated !== is) {
                      setIsTitleTruncated(is)
                    }
                  }
                }}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                className={css`
                  flex-basis: 100%;
                  min-width: 450px;
                  margin-right: 20px;

                  font-family: ${deprecatedTheme.fontFamilies.primary};
                  font-size: 21px;
                  font-weight: 500;
                  color: ${theme.color.neutral100};

                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                `}
              >
                <TitleString
                  type={type}
                  sourceIP={sourceIP}
                  sourceName={sourceName}
                  displayName={displayName}
                  copyButton={false}
                />
              </div>
            )}
          />
        </div>
        <div
          className={css`
            width: 75px;
            flex-shrink: 0;
            display: flex;
            justify-content: flex-end;

            visibility: visible;
            ${smallScreenMediaQuery} {
              visibility: hidden;
            }
          `}
        >
          {renderActions}
        </div>
      </div>
      {sourceName && (
        <div
          className={css`
            display: inline-flex;
            align-items: center;
            min-width: 0;
            margin-bottom: 10px;
            font-family: ${deprecatedTheme.fontFamilies.primaryCondensed};
            color: ${theme.color.neutral60};
            font-size: 18px;
          `}
        >
          <div>{sourceIP}</div>
        </div>
      )}
      <div
        className={css`
          display: inline-flex;
          align-items: center;
          min-width: 0;
          flex-wrap: wrap;
          & > * {
            margin-bottom: 10px;
          }
        `}
      >
        <Tags tags={tags} onRemoveTag={onRemoveTag} />
      </div>
      <div
        className={css`
          display: flex;
          align-items: center;
          margin-top: 5px;

          flex-direction: row;
          ${smallScreenMediaQuery} {
            flex-direction: column;
            align-self: flex-start;
          }
        `}
      >
        <EventMetadata
          received={received}
          result={result}
          url={url}
          server={server}
          application={application}
          httpRequest={httpRequest}
          attackers={attackers}
        />
      </div>
    </div>
  )
}

function Tags({
  tags,
  onRemoveTag,
  tagClassName
}: {
  readonly tags: string[]
  readonly onRemoveTag: (tag: string) => void
  readonly tagClassName?: string
}) {
  return (
    <Fragment>
      {tags.sort().map((tag, tagIndex) => (
        <Tag
          onRemove={() => onRemoveTag(tag)}
          key={tagIndex}
          className={tagClassName}
        >
          {tag}
        </Tag>
      ))}
    </Fragment>
  )
}
